import { useCallback, useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import type { PhaseContractTag } from '../../types';
import { contractTagApi } from '../../api/endpoints';
import { adminContractTagApi } from '../../api/adminApi';
import { getApiErrorMessage } from '../../api/client';
import { notify } from '../../components/Notifier';
import { ConfirmDialog } from './ConfirmDialog';

interface PhaseRef {
  id: string;
  name: string;
}

interface Props {
  /** Giai đoạn cần quản lý nhãn; null = đóng dialog. */
  phase: PhaseRef | null;
  onClose: () => void;
}

/**
 * Dialog CRUD danh sách nhãn hợp đồng (VD "Hợp đồng gốc", "Phụ lục 01") của
 * một giai đoạn. Sửa theo dòng (bấm bút chì → 2 ô nhập tại chỗ), không hỗ trợ
 * kéo-thả sắp xếp — chỉ nhập số thứ tự, giống PhaseManagement.
 */
export function PhaseContractTagsDialog({ phase, onClose }: Props): JSX.Element {
  const [tags, setTags] = useState<PhaseContractTag[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editLabel, setEditLabel] = useState('');
  const [editOrder, setEditOrder] = useState('0');
  const [newLabel, setNewLabel] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<PhaseContractTag | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    if (!phase) return;
    setLoading(true);
    try {
      const list = await contractTagApi.listByPhase(phase.id);
      setTags(list);
    } catch (err) {
      notify(getApiErrorMessage(err, 'Không thể tải danh sách nhãn hợp đồng'), 'error');
    } finally {
      setLoading(false);
    }
  }, [phase]);

  useEffect(() => {
    setEditingId(null);
    setNewLabel('');
    void load();
  }, [load]);

  const handleAdd = async () => {
    if (!phase || !newLabel.trim()) return;
    setSaving(true);
    try {
      await adminContractTagApi.create(phase.id, { label: newLabel.trim() });
      setNewLabel('');
      notify('Đã thêm nhãn hợp đồng', 'success');
      await load();
    } catch (err) {
      notify(getApiErrorMessage(err, 'Không thể thêm nhãn hợp đồng'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (tag: PhaseContractTag) => {
    setEditingId(tag.id);
    setEditLabel(tag.label);
    setEditOrder(String(tag.order_index));
  };

  const cancelEdit = () => setEditingId(null);

  const handleSaveEdit = async (id: string) => {
    if (!editLabel.trim()) {
      notify('Vui lòng nhập tên nhãn', 'warning');
      return;
    }
    const orderIndex = Number(editOrder);
    if (Number.isNaN(orderIndex)) {
      notify('Thứ tự phải là số', 'warning');
      return;
    }
    setSaving(true);
    try {
      await adminContractTagApi.update(id, { label: editLabel.trim(), order_index: orderIndex });
      setEditingId(null);
      notify('Đã cập nhật nhãn hợp đồng', 'success');
      await load();
    } catch (err) {
      notify(getApiErrorMessage(err, 'Không thể cập nhật nhãn hợp đồng'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await adminContractTagApi.remove(deleteTarget.id);
      notify('Đã xóa mềm nhãn hợp đồng', 'success');
      setDeleteTarget(null);
      await load();
    } catch (err) {
      notify(getApiErrorMessage(err, 'Không thể xóa nhãn hợp đồng'), 'error');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <Dialog open={phase !== null} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Quản lý loại hợp đồng: {phase?.name}</DialogTitle>
        <DialogContent dividers>
          {loading ? (
            <Stack alignItems="center" sx={{ py: 3 }}>
              <CircularProgress size={24} />
            </Stack>
          ) : (
            <>
              {tags.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Chưa có nhãn hợp đồng nào cho giai đoạn này.
                </Typography>
              )}
              {tags.length > 0 && (
                <Table size="small" sx={{ mb: 2 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell width={72}>Thứ tự</TableCell>
                      <TableCell>Tên nhãn</TableCell>
                      <TableCell align="right">Hành động</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tags.map((tag) =>
                      editingId === tag.id ? (
                        <TableRow key={tag.id}>
                          <TableCell>
                            <TextField
                              size="small"
                              type="number"
                              value={editOrder}
                              onChange={(e) => setEditOrder(e.target.value)}
                              sx={{ width: 64 }}
                              disabled={saving}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              value={editLabel}
                              onChange={(e) => setEditLabel(e.target.value)}
                              fullWidth
                              disabled={saving}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <IconButton size="small" onClick={() => void handleSaveEdit(tag.id)} disabled={saving}>
                              <CheckIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={cancelEdit} disabled={saving}>
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ) : (
                        <TableRow key={tag.id} hover>
                          <TableCell>{tag.order_index}</TableCell>
                          <TableCell>{tag.label}</TableCell>
                          <TableCell align="right">
                            <IconButton size="small" onClick={() => startEdit(tag)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={() => setDeleteTarget(tag)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ),
                    )}
                  </TableBody>
                </Table>
              )}
              <Stack direction="row" spacing={1}>
                <TextField
                  size="small"
                  label="Thêm nhãn mới"
                  value={newLabel}
                  onChange={(e) => setNewLabel(e.target.value)}
                  fullWidth
                  disabled={saving}
                />
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => void handleAdd()}
                  disabled={saving || !newLabel.trim()}
                >
                  Thêm
                </Button>
              </Stack>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Đóng</Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={deleteTarget !== null}
        title="Xóa mềm nhãn hợp đồng"
        message={`Nhãn "${deleteTarget?.label}" sẽ bị ẩn khỏi danh sách lựa chọn (xóa mềm). Bạn có chắc chắn muốn tiếp tục?`}
        confirmLabel="Xóa mềm"
        loading={deleting}
        onConfirm={() => void handleDelete()}
        onCancel={() => setDeleteTarget(null)}
      />
    </>
  );
}
